import type { Item, ItemDetailResponse, PurchaseEvent } from "@kaidoki/proto";
import { createContext, useCallback, useContext, useReducer } from "react";
import { compareByStatus } from "../lib/status";
import { getActiveGroupIdRef } from "./GroupContext";

const PERSONAL_KEY = "__personal";

interface CacheEntry {
  items: ItemDetailResponse[];
  timestamp: number;
}

type CacheState = Map<string, CacheEntry>;

type Action =
  | { type: "SET_ITEMS"; groupId: string; items: ItemDetailResponse[] }
  | { type: "ADD_ITEM"; groupId: string; item: ItemDetailResponse }
  | {
      type: "UPDATE_DUE_DATE";
      groupId: string;
      itemId: string;
      nextDueAt: string;
      purchaseEvent?: PurchaseEvent;
    }
  | { type: "REPLACE_ITEM"; groupId: string; item: Item }
  | { type: "REMOVE_ITEM"; groupId: string; itemId: string }
  | { type: "CLEAR_GROUP"; groupId: string }
  | { type: "CLEAR_ALL" };

function cacheReducer(state: CacheState, action: Action): CacheState {
  const next = new Map(state);
  switch (action.type) {
    case "SET_ITEMS":
      next.set(action.groupId, {
        items: action.items,
        timestamp: Date.now(),
      });
      return next;
    case "ADD_ITEM": {
      const entry = next.get(action.groupId);
      const list = entry ? [...entry.items, action.item] : [action.item];
      list.sort((a, b) =>
        compareByStatus(a.item?.nextDueAt ?? "", b.item?.nextDueAt ?? ""),
      );
      next.set(action.groupId, { items: list, timestamp: Date.now() });
      return next;
    }
    case "UPDATE_DUE_DATE": {
      const entry = next.get(action.groupId);
      if (!entry) return state;
      const items = entry.items.map((detail) => {
        if (detail.item?.id !== action.itemId) return detail;
        return {
          ...detail,
          item: { ...detail.item, nextDueAt: action.nextDueAt },
          purchaseHistory: action.purchaseEvent
            ? [action.purchaseEvent, ...detail.purchaseHistory]
            : detail.purchaseHistory,
        };
      });
      items.sort((a, b) =>
        compareByStatus(a.item?.nextDueAt ?? "", b.item?.nextDueAt ?? ""),
      );
      next.set(action.groupId, { items, timestamp: Date.now() });
      return next;
    }
    case "REPLACE_ITEM": {
      const entry = next.get(action.groupId);
      if (!entry) return state;
      const items = entry.items.map((detail) =>
        detail.item?.id === action.item.id
          ? { ...detail, item: action.item }
          : detail,
      );
      items.sort((a, b) =>
        compareByStatus(a.item?.nextDueAt ?? "", b.item?.nextDueAt ?? ""),
      );
      next.set(action.groupId, { items, timestamp: Date.now() });
      return next;
    }
    case "REMOVE_ITEM": {
      const entry = next.get(action.groupId);
      if (!entry) return state;
      next.set(action.groupId, {
        items: entry.items.filter((d) => d.item?.id !== action.itemId),
        timestamp: Date.now(),
      });
      return next;
    }
    case "CLEAR_GROUP":
      next.delete(action.groupId);
      return next;
    case "CLEAR_ALL":
      return new Map();
  }
}

interface ItemsCacheContextValue {
  getItems: (groupId?: string | null) => ItemDetailResponse[] | null;
  getItemDetail: (
    itemId: string,
    groupId?: string | null,
  ) => ItemDetailResponse | null;
  setItems: (items: ItemDetailResponse[], groupId?: string | null) => void;
  addItem: (item: ItemDetailResponse, groupId?: string | null) => void;
  updateDueDate: (
    itemId: string,
    nextDueAt: string,
    purchaseEvent?: PurchaseEvent,
    groupId?: string | null,
  ) => void;
  replaceItem: (item: Item, groupId?: string | null) => void;
  removeItem: (itemId: string, groupId?: string | null) => void;
  clearGroup: (groupId?: string | null) => void;
  clearAll: () => void;
}

const ItemsCacheContext = createContext<ItemsCacheContextValue | null>(null);

function resolveKey(groupId?: string | null): string {
  return groupId ?? getActiveGroupIdRef() ?? PERSONAL_KEY;
}

export function ItemsCacheProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(
    cacheReducer,
    new Map<string, CacheEntry>(),
  );

  const getItems = useCallback(
    (groupId?: string | null): ItemDetailResponse[] | null => {
      const entry = state.get(resolveKey(groupId));
      return entry ? entry.items : null;
    },
    [state],
  );

  const getItemDetail = useCallback(
    (itemId: string, groupId?: string | null): ItemDetailResponse | null => {
      const entry = state.get(resolveKey(groupId));
      return entry?.items.find((d) => d.item?.id === itemId) ?? null;
    },
    [state],
  );

  const setItems = useCallback(
    (items: ItemDetailResponse[], groupId?: string | null) => {
      dispatch({ type: "SET_ITEMS", groupId: resolveKey(groupId), items });
    },
    [],
  );

  const addItem = useCallback(
    (item: ItemDetailResponse, groupId?: string | null) => {
      dispatch({ type: "ADD_ITEM", groupId: resolveKey(groupId), item });
    },
    [],
  );

  const removeItem = useCallback((itemId: string, groupId?: string | null) => {
    dispatch({ type: "REMOVE_ITEM", groupId: resolveKey(groupId), itemId });
  }, []);

  const updateDueDate = useCallback(
    (
      itemId: string,
      nextDueAt: string,
      purchaseEvent?: PurchaseEvent,
      groupId?: string | null,
    ) => {
      dispatch({
        type: "UPDATE_DUE_DATE",
        groupId: resolveKey(groupId),
        itemId,
        nextDueAt,
        purchaseEvent,
      });
    },
    [],
  );

  const replaceItem = useCallback((item: Item, groupId?: string | null) => {
    dispatch({ type: "REPLACE_ITEM", groupId: resolveKey(groupId), item });
  }, []);

  const clearGroup = useCallback((groupId?: string | null) => {
    dispatch({ type: "CLEAR_GROUP", groupId: resolveKey(groupId) });
  }, []);

  const clearAll = useCallback(() => {
    dispatch({ type: "CLEAR_ALL" });
  }, []);

  return (
    <ItemsCacheContext.Provider
      value={{
        getItems,
        getItemDetail,
        setItems,
        addItem,
        updateDueDate,
        replaceItem,
        removeItem,
        clearGroup,
        clearAll,
      }}
    >
      {children}
    </ItemsCacheContext.Provider>
  );
}

export function useItemsCache(): ItemsCacheContextValue {
  const ctx = useContext(ItemsCacheContext);
  if (!ctx) {
    throw new Error("useItemsCache must be used within ItemsCacheProvider");
  }
  return ctx;
}
